import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast, { Toaster } from 'react-hot-toast';
import { Lock, Mail, Eye, EyeOff, ArrowLeft, ShieldCheck } from 'lucide-react';
import { authAPI } from '../../utils/adminAPI';
import { sucessoComHorario } from '../../utils/toastComHorario';

const LoginAdmin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const { register, handleSubmit, formState: { errors } } = useForm();

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      await authAPI.login(data.email, data.password);
      sucessoComHorario('Login realizado com sucesso!');
      navigate('/admin');
    } catch (error) {
      toast.error(error.response?.data?.error || error.userMessage || 'E-mail ou senha inválidos');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-corumba-light">
      <Toaster position="top-center" />

      {/* Painel institucional — só aparece a partir de lg, some no mobile
          pra não competir com o formulário na tela pequena */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-gradient-corumba">
        {/* Blobs decorativos, bem sutis, dão movimento sem distrair */}
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-[#40A1E6]/20 rounded-full blur-3xl animate-pulse-slow" />
        <div className="absolute bottom-0 -right-16 w-80 h-80 bg-amber-400/10 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }} />

        <img
          src="/assets/background_corumba.png"
          alt=""
          aria-hidden="true"
          className="absolute bottom-0 left-0 w-full h-auto opacity-20 object-cover"
        />

        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <Link to="/" className="self-start animate-fade-in-up">
            <img
              src="/assets/logo_prefeitura_horizontal.png"
              alt="Prefeitura de Corumbá — voltar à página inicial"
              className="h-12 w-auto hover:opacity-90 transition-opacity"
            />
          </Link>

          <div className="animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-6 text-sm">
              <ShieldCheck className="w-4 h-4 text-[#7dc4f2]" />
              Acesso restrito à equipe gestora
            </div>
            <h1 className="text-4xl font-bold leading-tight mb-4">
              Painel de Gestão<br />das Enquetes Públicas
            </h1>
            <p className="text-primary-100 text-lg max-w-md">
              Crie, publique e acompanhe os resultados das enquetes que dão voz à população de Corumbá.
            </p>
          </div>

          <p className="text-sm text-primary-200">
            © 2026 Prefeitura Municipal de Corumbá
          </p>
        </div>
      </div>

      {/* Formulário */}
      <div className="flex-1 flex items-center justify-center px-4 py-12 sm:px-8">
        <div className="w-full max-w-sm animate-fade-in-up">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-xl shadow-primary-900/10 p-8 sm:p-10">
            {/* Cabeçalho compacto — só no mobile, onde não há o painel lateral */}
            <div className="flex flex-col items-center mb-8 lg:hidden">
              <div className="bg-primary-600 text-white rounded-2xl p-3.5 mb-4 shadow-lg shadow-primary-600/30">
                <Lock className="w-6 h-6" />
              </div>
              <h1 className="text-xl font-bold text-gray-900">Painel Administrativo</h1>
              <p className="text-sm text-gray-500">Sistema de Enquetes - Corumbá</p>
            </div>

            <div className="hidden lg:block mb-8">
              <h2 className="text-2xl font-bold text-gray-900">Entrar</h2>
              <p className="text-gray-500 mt-1">Acesse com suas credenciais de gestor.</p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  E-mail
                </label>
                <div className="relative">
                  <Mail className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <input
                    {...register('email', {
                      required: 'E-mail é obrigatório',
                      pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Informe um e-mail válido' }
                    })}
                    type="email"
                    autoComplete="email"
                    autoFocus
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 pl-11 pr-4 py-3 text-gray-900 placeholder:text-gray-400 focus:bg-white focus:border-[#40A1E6] focus:ring-4 focus:ring-[#40A1E6]/15 outline-none transition-all duration-200"
                    placeholder="seunome@corumba.ms.gov.br"
                  />
                </div>
                {errors.email && (
                  <p className="text-sm text-red-600 mt-1.5">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Senha
                </label>
                <div className="relative">
                  <Lock className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <input
                    {...register('password', { required: 'Senha é obrigatória' })}
                    type={mostrarSenha ? 'text' : 'password'}
                    autoComplete="current-password"
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 pl-11 pr-11 py-3 text-gray-900 placeholder:text-gray-400 focus:bg-white focus:border-[#40A1E6] focus:ring-4 focus:ring-[#40A1E6]/15 outline-none transition-all duration-200"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setMostrarSenha((v) => !v)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label={mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'}
                    tabIndex={-1}
                  >
                    {mostrarSenha ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-sm text-red-600 mt-1.5">{errors.password.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3.5 shadow-lg shadow-primary-600/25 hover:shadow-xl hover:shadow-primary-600/30 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Entrando...
                  </>
                ) : (
                  'Entrar'
                )}
              </button>
            </form>
          </div>

          <Link
            to="/"
            className="mt-6 flex items-center justify-center gap-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-full px-5 py-2.5 shadow-sm hover:border-[#40A1E6] hover:text-[#40A1E6] hover:bg-[#40A1E6]/5 hover:shadow-md transition-all duration-200"
          >
            <ArrowLeft className="w-4 h-4" /> Voltar à Página Inicial
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LoginAdmin;
